/* eslint-disable @typescript-eslint/no-explicit-any */
import { File } from 'better-xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { ICacUsageMetricValues } from '../models/statistics.model';
import type { ICacExportMatrix, ICacExportRow } from './cacUsageTabRows';

export type { ICacExportMatrix, ICacExportRow } from './cacUsageTabRows';

const COLOR_HEADER_BG = 'BB0665';
const COLOR_WHITE = 'FFFFFFFF';

/** 데이터 행 높이 — exportTable.js와 동일 */
const ROW_HEIGHT_CM = 0.8;

/** exportTable.js 기본 `cellWidths = [20, 20, 20, 20]` 와 동일 */
const cellWidths = [20, 20, 20, 20];

const resolveColWidth = (index: number) =>
  cellWidths[index] ?? cellWidths[cellWidths.length - 1];

export interface ICacExportWorkbookTab {
  sheetName: string;
  rowHeaderLabel: string;
  reportRows: ICacExportRow[];
  matrix?: ICacExportMatrix;
}

const fmtNum = (n: number | null | undefined, suffix: string) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  return `${n.toLocaleString('ko-KR')} ${suffix}`;
};

const fmtPct = (n: number | null | undefined) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  return `${n.toLocaleString('ko-KR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const formatDate = (d?: string) => {
  if (!d) return '';
  if (d.length === 8) return moment(d, 'YYYYMMDD').format('YYYY-MM-DD');
  return d;
};

const buildConditionText = (
  excludeUpperLevels: boolean,
  excludeMiddleLevels: boolean,
) => {
  const parts: string[] = [];
  if (excludeUpperLevels) parts.push('Deca·Hendeca 제외');
  if (excludeMiddleLevels) parts.push('중등 제외');
  return parts.join(', ');
};

const setHeaderStyle = (cell: any) => {
  cell.style.fill.patternType = 'solid';
  cell.style.fill.fgColor = COLOR_HEADER_BG;
  cell.style.fill.bgColor = COLOR_HEADER_BG;
  cell.style.font.color = COLOR_WHITE;
  cell.style.font.bold = true;
  cell.style.align.h = 'center';
  cell.style.align.v = 'center';
};

const setDataStyle = (
  cell: any,
  align: 'center' | 'right' | 'left' = 'center',
) => {
  cell.style.align.h = align;
  cell.style.align.v = 'center';
};

/** 엑셀 시트명: 금지 문자 제거 + 31자 제한 */
const sanitizeSheetName = (raw: string, used: Set<string>): string => {
  const invalid = /[\\/:*?\[\]]/g;
  let base = (raw || 'Sheet').replace(invalid, '_').trim().slice(0, 31);
  if (!base) base = 'Sheet';
  let name = base;
  let n = 2;
  while (used.has(name)) {
    const suffix = `_${n}`;
    name = (base.slice(0, Math.max(1, 31 - suffix.length)) + suffix).slice(
      0,
      31,
    );
    n += 1;
  }
  used.add(name);
  return name;
};

const appendCacUsageSheet = (
  sheet: any,
  tab: ICacExportWorkbookTab,
  meta: {
    srtDate: string;
    endDate: string;
    baseDate?: string;
    excludeUpperLevels: boolean;
    excludeMiddleLevels: boolean;
  },
) => {
  const { srtDate, endDate, baseDate, excludeUpperLevels, excludeMiddleLevels } =
    meta;
  const { rowHeaderLabel, reportRows, matrix } = tab;

  const addHeaderRow = (
    label: string,
    value: string,
    valueHorizontal: 'center' | 'left' = 'center',
  ) => {
    const row = sheet.addRow();
    row.setHeightCM(ROW_HEIGHT_CM);
    const c1 = row.addCell();
    c1.value = label;
    setHeaderStyle(c1);
    const c2 = row.addCell();
    c2.value = value;
    setDataStyle(c2, valueHorizontal);
  };
  addHeaderRow('학생 기준일', formatDate(baseDate || endDate));
  addHeaderRow(
    '조회기간',
    `${formatDate(srtDate)} ~ ${formatDate(endDate)}`,
    'left',
  );
  addHeaderRow(
    '조회 조건',
    buildConditionText(excludeUpperLevels, excludeMiddleLevels),
  );

  sheet.addRow().setHeightCM(ROW_HEIGHT_CM);

  if (matrix) {
    const mh = sheet.addRow();
    mh.setHeightCM(ROW_HEIGHT_CM);
    ['', 'CANB 전체', matrix.divisionLabel, matrix.campusLabel].forEach(
      (title) => {
        const c = mh.addCell();
        c.value = title;
        setHeaderStyle(c);
      },
    );

    const matrixRowDefs: Array<{
      label: string;
      key: keyof ICacUsageMetricValues;
    }> = [
      { label: '사용률', key: 'usageRate' },
      { label: '학생 고유 사용률', key: 'uniqueStudentRate' },
      { label: '담임 고유 사용률', key: 'homeroomUniqueRate' },
    ];
    matrixRowDefs.forEach((def) => {
      const r = sheet.addRow();
      r.setHeightCM(ROW_HEIGHT_CM);
      const c0 = r.addCell();
      c0.value = def.label;
      setHeaderStyle(c0);

      const cellA = r.addCell();
      cellA.value = fmtPct(matrix.canbAll?.[def.key]);
      setDataStyle(cellA, 'right');

      const cellD = r.addCell();
      cellD.value = fmtPct(matrix.divisionSum?.[def.key]);
      setDataStyle(cellD, 'right');

      const cellC = r.addCell();
      cellC.value = fmtPct(matrix.campus?.[def.key]);
      setDataStyle(cellC, 'right');
    });

    sheet.addRow().setHeightCM(ROW_HEIGHT_CM);
  }

  const mainHeaderTitles = [
    rowHeaderLabel,
    '전체 학생수',
    '총 조회수',
    '사용률',
    '학생 고유 조회수',
    '학생 고유 사용률',
    '담임 고유 조회수',
    '담임 고유 사용률',
  ];
  const mainHeader = sheet.addRow();
  mainHeader.setHeightCM(ROW_HEIGHT_CM);
  mainHeaderTitles.forEach((title) => {
    const c = mainHeader.addCell();
    c.value = title;
    setHeaderStyle(c);
  });

  reportRows.forEach((row) => {
    const r = sheet.addRow();
    r.setHeightCM(ROW_HEIGHT_CM);
    const c0 = r.addCell();
    c0.value = row.label;
    setDataStyle(c0, 'center');

    const dataCells = [
      fmtNum(row.metrics.enrolledCount, '명'),
      fmtNum(row.metrics.totalViews, '건'),
      fmtPct(row.metrics.usageRate),
      fmtNum(row.metrics.uniqueStudentViews, '명'),
      fmtPct(row.metrics.uniqueStudentRate),
      fmtNum(row.metrics.homeroomUniqueViews, '명'),
      fmtPct(row.metrics.homeroomUniqueRate),
    ];
    dataCells.forEach((v) => {
      const c = r.addCell();
      c.value = v;
      setDataStyle(c, 'right');
    });
  });

  const colCount = mainHeaderTitles.length;
  for (let i = 0; i < colCount; i += 1) {
    sheet.col(i).width = resolveColWidth(i);
  }
};

/**
 * 활용 통계 — 탭마다 시트 1개, 한 파일(.xlsx)로 저장
 */
const exportCacReportWorkbook = (
  meta: {
    srtDate: string;
    endDate: string;
    baseDate?: string;
    excludeUpperLevels: boolean;
    excludeMiddleLevels: boolean;
  },
  tabs: ICacExportWorkbookTab[],
) => {
  if (!tabs.length) return;

  const file = new File();
  const usedNames = new Set<string>();
  tabs.forEach((tab) => {
    const sheetName = sanitizeSheetName(tab.sheetName, usedNames);
    const sheet = file.addSheet(sheetName);
    appendCacUsageSheet(sheet, tab, meta);
  });

  const fileName = `CAC 활용 통계_전체_${moment().format('YYYYMMDD')}`;
  file.saveAs('blob').then((content: Blob) => {
    saveAs(content, `${fileName}.xlsx`);
  });
};

export default exportCacReportWorkbook;
